import React, { useState, useEffect } from 'react';
import { List, Card, Row, Col, Spin, Button, notification } from 'antd';
import axios from 'axios';
import { API_URL } from '../../apis/apiCotizaciones';
import { API_URL as ESTADO_API_URL } from '../../apis/apiEstados';
import './css-admin/cotizaciones.css';

const Cotizaciones = () => {
  const [cotizaciones, setCotizaciones] = useState([]); // Para almacenar las cotizaciones
  const [filteredCotizaciones, setFilteredCotizaciones] = useState([]); // Para las cotizaciones filtradas
  const [loading, setLoading] = useState(true); // Para el estado de carga
  const [estados, setEstados] = useState([]); // Para almacenar los estados
  const [estadoSeleccionado, setEstadoSeleccionado] = useState(null); // Estado seleccionado para filtrar

  // Obtener las cotizaciones y los estados al cargar el componente
  useEffect(() => {
    const fetchCotizaciones = async () => {
      try {
        const response = await axios.get(`${API_URL}`);
        setCotizaciones(response.data);
        setFilteredCotizaciones(response.data); // Inicialmente, todas las cotizaciones son mostradas
      } catch (error) {
        console.error('Error al obtener las cotizaciones:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchEstados = async () => {
      try {
        const response = await axios.get(ESTADO_API_URL);
        setEstados(response.data);
      } catch (error) {
        console.error('Error al obtener los estados:', error);
      }
    };

    fetchCotizaciones();
    fetchEstados();
  }, []);

  // Función para cambiar el estado de la cotización
  const cambiarEstadoCotizacion = async (cotizacionId, nuevoEstadoId) => {
    try {
      await axios.patch(`${API_URL}${cotizacionId}/`, { estado: nuevoEstadoId });

      // Actualizar localmente el estado de la cotización
      setCotizaciones(cotizaciones.map(cotizacion =>
        cotizacion.id === cotizacionId ? { ...cotizacion, estado: nuevoEstadoId } : cotizacion
      ));
      // Actualizar las cotizaciones filtradas si el estado seleccionado es el mismo
      if (estadoSeleccionado === nuevoEstadoId) {
        setFilteredCotizaciones(filteredCotizaciones.map(cotizacion =>
          cotizacion.id === cotizacionId ? { ...cotizacion, estado: nuevoEstadoId } : cotizacion
        ));
      }

      notification.success({
        message: 'Estado actualizado',
        description: `El estado de la cotización ha sido cambiado a ${estados.find(estado => estado.id === nuevoEstadoId)?.descripcion}.`,
        placement: 'topRight',
      });
    } catch (error) {
      console.error('Error al cambiar el estado de la cotización:', error);
      notification.error({
        message: 'Error',
        description: 'Hubo un error al cambiar el estado de la cotización.',
        placement: 'topRight',
      });
    }
  };

  // Filtrar las cotizaciones según el estado seleccionado
  const filtrarCotizacionesPorEstado = (estadoId) => {
    setEstadoSeleccionado(estadoId);
    const cotizacionesFiltradas = estadoId ? 
      cotizaciones.filter(cotizacion => cotizacion.estado === estadoId) : 
      cotizaciones; // Si no hay estado seleccionado, mostrar todas las cotizaciones
    setFilteredCotizaciones(cotizacionesFiltradas);
  };

  // Filtrar los estados permitidos (solo ID 3, 4 y 5)
  const estadosPermitidos = estados.filter(estado => [3, 4, 5].includes(estado.id));

  // Mapeo de colores para cada estado
  const colorEstado = {
    3: '#367c29', // Pendiente
    4: '#28661f',   // En proceso
    5: '#0d390a',  // Resuelta
  };

  // Renderizado de cada cotización con productos y servicios
  return (
    <div style={{ padding: '20px' }}>
      <div className='cotizaciones-admin-css'>
        <h2 style={{ textAlign: 'center' }}>Listado de Cotizaciones</h2>

        {/* Filtro por estado */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Button
            type={estadoSeleccionado === null ? 'primary' : 'default'}
            onClick={() => filtrarCotizacionesPorEstado(null)} // Mostrar todas las cotizaciones
          >
            Ver Todas
          </Button>
          {estadosPermitidos.map(estado => (
            <Button
              key={estado.id}
              type={estadoSeleccionado === estado.id ? 'primary' : 'default'}
              style={{ marginLeft: '10px' }}
              onClick={() => filtrarCotizacionesPorEstado(estado.id)} // Filtrar por estado
            >
              {estado.descripcion}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <Spin tip="Cargando cotizaciones..." size="large" />
      ) : (
        <Row gutter={[16, 16]} justify="center"> {/* Añadimos justify para centrar las cartas */}
          {filteredCotizaciones.map((cotizacion) => (
            <Col key={cotizacion.id} xs={24} sm={12} md={12} lg={12} xl={12} style={{ display: 'flex', justifyContent: 'center' }}>
              <Card 
                style={{ 
                  width: '100%', 
                  marginBottom: '20px',
                  border: '1px solid #494949', // Borde más grueso
                  boxShadow: '0 4px 8px rgba(0.4, 0.4, 0.4, 0.4)', // Sombra
                  borderRadius: '8px' // Bordes redondeados
                }}
              >
                <h3 style={{ fontWeight: 'bold', color: '#1890ff' }}>Cotización #{cotizacion.id}</h3>
                <p><strong>Nombre Completo:</strong> {cotizacion.nombreCompleto}</p>
                <p><strong>Correo:</strong> {cotizacion.correo}</p>
                <p><strong>Teléfono:</strong> {cotizacion.telefono}</p>
                <p style={{ color: colorEstado[cotizacion.estado] }}>
                  <strong>Estado:</strong> {estados.find(estado => estado.id === cotizacion.estado)?.descripcion}
                </p>

                {/* Botones para cambiar el estado, solo para estados permitidos */}
                <div style={{ marginTop: '10px' , textAlign: 'center'}}>
                  {estadosPermitidos.map(estado => (
                    <Button
                      key={estado.id}
                      style={{
                        marginRight: '10px',
                        backgroundColor: colorEstado[estado.id],
                        color: 'white', // Texto en blanco para mayor contraste
                      }}
                      onClick={() => cambiarEstadoCotizacion(cotizacion.id, estado.id)}
                    >
                      Cambiar a {estado.descripcion}
                    </Button>
                  ))}
                </div>

                {/* Productos relacionados */}
                {cotizacion.productos && cotizacion.productos.length > 0 && (
                  <>
                    <h4 style={{ marginTop: '20px' }}>Productos</h4>
                    <Row gutter={[16, 16]}>
                      {cotizacion.productos.map((producto) => (
                        <Col key={producto.id} xs={24} sm={12} md={8} lg={6}>
                          <Card
                          style={{ 
                            width: '100%', 
                            marginBottom: '20px',
                            border: '1px solid #494949', // Borde más grueso
                            boxShadow: '0 4px 8px rgba(0.4, 0.4, 0.4, 0.4)', // Sombra
                            borderRadius: '8px' // Bordes redondeados
                          }}>
                            <Card.Meta
                              title={<strong>{producto.nombre}</strong>}
                              description={
                                <>
                                  <p>Modelo: {producto.modelo}</p>
                                  <p>Descripción: {producto.descripcion}</p>
                                </>
                              }
                            />
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </>
                )}

                {/* Servicios relacionados */}
                {cotizacion.servicios && cotizacion.servicios.length > 0 && (
                  <>
                    <h4 style={{ marginTop: '20px' }}>Servicios</h4>
                    <List
                      itemLayout="horizontal"
                      dataSource={cotizacion.servicios}
                      renderItem={(servicio) => (
                        <List.Item>
                          <List.Item.Meta
                            title={<strong>{servicio.tipo === 1 ? 'Mantenimiento' : 'Reparación'}</strong>}
                            description={
                              <>
                                <p><strong>Descripción:</strong> {servicio.descripcion}</p>
                              </>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default Cotizaciones;
